/** Tipurile si formatarile comune ale ecranului Rambursuri curieri. */
export interface Ramburs {
  id: number
  courier: string
  awbNo: string
  transferDate: string
  awbDate: string | null
  amountCollected: number
  recipientName: string | null
  recipientLocality: string | null
  content: string | null
  comandaNo: string | null
  eaDocType: string | null
  eaDocNo: string | null
  partnerName: string | null
  invoiceDate: string | null
  invoiceTotal: number | null
  feeAmount: number | null
  status: string
  /** Ce fel de DESTINATAR are coletul. `dropship` = factură pe firmă, colet la persoană. */
  tipClient: 'firma' | 'dropship' | 'persoana'
  matchError: string | null
  matchErrorLabel: string | null
  postedAt: string | null
  /**
   * Se poate trimite in EA acum: potrivit, sau ramas agatat „in trimitere" peste 10 minute.
   * Calculat in API (`esteDeTrimis`), aceeasi regula ca lotul si numaratoarea (#810).
   */
  deTrimis: boolean
  /** Raspunsul EA la ultima trimitere, sau motivul pentru care nu s-a trimis. */
  eaResponse: string | null
  /** De ce a fost închis fără să fie potrivit (închidere în masă a unui an vechi). */
  closedReason: string | null
}

export interface ListResponse {
  rows: Ramburs[]
  total: number
  /** Cate incasari in fiecare stare, cu celelalte filtre — pentru chip-uri. */
  peStare: Record<string, number>
  byStatus: { status: string; count: number; amount: number }[]
  totalTaxe: number
}

export interface TranzactieBanca {
  id: number
  date: string
  amount: number
}

export interface ZiVirament {
  date: string
  incasari: number
  colectat: number
  primit: number
  diferenta: number
  banca: TranzactieBanca[]
  status: 'confirmat' | 'neconfirmat'
}

export interface ViramenteResp {
  zile: ZiVirament[]
  total: { colectat: number; primit: number; diferenta: number; zileConfirmate: number }
  bancaNealocata: TranzactieBanca[]
  extrasPanaLa: string | null
}

export interface Expeditie {
  id: number
  courier: string
  awbNo: string
  awbDate: string | null
  content: string | null
  comandaNo: string | null
  recipientName: string | null
  recipientLocality: string | null
  lastEventName: string | null
  lastEventLocation: string | null
  lastEventAt: string | null
  deliveredAt: string | null
  /** Zile lucratoare la curier, peste pragul unic de intarziere (4); altfel null. Din API. */
  intarziere: number | null
  pickedUpAt: string | null
  /** Preluare estimata — colet Sameday aflat deja pe drum cand a aparut campul. */
  pickedUpApprox: boolean
  etichetaStare: string | null
  deliveredTo: string | null
  returnAwbNumber: string | null
  blockedSince: string | null
  /** Sameday n-are AWB de retur: finalul e o stare, iar `closedAt` spune că s-a terminat. */
  closedAt: string | null
  /** Motivul închiderii manuale. Gol când închiderea a venit dintr-o stare a curierului. */
  closeNote: string | null
  inReturn: boolean
  /** Starea comuna din API — vezi `lib/stareExpeditie.ts`. */
  stare: string | null
  /** Livrat (semnat), dar curierul n-a scanat livrarea in urmarire (`fan/scanareLivrare.ts`). */
  faraScanareLivrare?: boolean
}

export interface ExpeditiiResp {
  rows: Expeditie[]
  total: number
  totalExpeditii: number
  blocate: number
  /** Cate colete in fiecare stare (`toate`, `intarziate`, `in_tranzit`…), pentru chip-uri. */
  peStare: Record<string, number>
}

export interface ExpeditieDetaliu extends Expeditie {
  evenimente: { id: number; at: string; name: string; location: string | null }[]
  /** Urgentarea ceruta curierului (28.10.2026): `cerut` o data, sau `exclus` cu motivul. */
  urgentari: {
    fel: 'cerut' | 'exclus'
    motiv: string | null
    createdAt: string
    trimitere: { catre: string[]; createdAt: string } | null
  }[]
  comandaShopify: { id: number; name: string } | null
  comandaCrm: { id: number; code: string } | null
  incasari: {
    id: number
    transferDate: string
    amountCollected: number
    status: string
    eaDocNo: string | null
    postedAt: string | null
  }[]
}

export interface DeRezolvat {
  coleteIntarziate: number
  dePotrivit: number
  deTrimis: { n: number; suma: number }
}

export interface PrevizualizareSelectie {
  seTrimit: { n: number; suma: number }
  dejaIncasate: { n: number; suma: number }
  partiale: { n: number; suma: number }
  neeligibile: number
}

export interface Candidat {
  docType: string
  docNo: string
  docns: string | null
  date: string
  partnerName: string | null
  totalWithVat: number | null
  zileInainte: number
}

export interface FanStatus {
  configurat: boolean
  deTrimis: number
  trimise: number
  enabled: boolean
  lastSyncedDate: string | null
  lastSyncAt: string | null
  lastError: string | null
  newPaymentsConfigurat: boolean
  sameday: { configurat: boolean; lastSyncAt: string | null; lastError: string | null }
  /** Trimiterea zilnica in EA (#812) — vezi `modules/fan/trimitereAutomata.ts` in API. */
  trimitereAutomata?: TrimitereAutomataStare
}

export interface TrimitereAutomataStare {
  pornita: boolean
  ora: string
  ultimaLa: string | null
  ultimulRezultat: {
    eroare?: string
    trimise?: number
    esuate?: number
    partiale?: number
    dejaIncasate?: number
  } | null
}

export const STATUS_LABEL: Record<string, string> = {
  deja_incasat: 'Era deja încasată',
  potrivit: 'Potrivit',
  exceptie: 'Nepotrivit',
  trimis: 'Trimis în EA',
  // Revendicat de o trimitere chiar acum — vezi `IN_TRIMITERE` in fanPost.service.ts.
  in_trimitere: 'Se trimite în EA',
  nou: 'Nou',
  inchis_an_vechi: 'Închis (an vechi)',
}

/** Starile unei incasari, pe ecran, in limbajul omului — aceleasi peste tot. */
export const STARI_INCASARE = [
  { cheie: 'exceptie', eticheta: 'De potrivit' },
  { cheie: 'potrivit', eticheta: 'De trimis' },
  { cheie: 'trimis', eticheta: 'Trimise în EA' },
  { cheie: 'deja_incasat', eticheta: 'Erau deja încasate' },
  { cheie: 'inchis_an_vechi', eticheta: 'Închise' },
  /*
    Toate starile deodata (Alexandru, 21.10.2026): rambursul cautat dupa client nu se vedea, fiindca
    era in alta stare decat cea aleasa — trebuia ghicit unde.
  */
  { cheie: 'toate', eticheta: 'Toate' },
] as const
export type StareIncasare = (typeof STARI_INCASARE)[number]['cheie']

/** Descarca un fisier prin axios: autentificarea e cu token in antet, nu cu cookie. */
export async function descarcaFisier(url: string, nume: string): Promise<void> {
  const { api } = await import('../../../lib/api')
  const res = await api.get(url, { responseType: 'blob' })
  const href = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = href
  a.download = nume
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

export const STATUS_CLASS: Record<string, string> = {
  deja_incasat: 'bg-b4l-bg text-b4l-text-muted border-b4l-border',
  inchis_an_vechi: 'bg-b4l-bg text-b4l-text-muted border-b4l-border',
  potrivit: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_trimitere: 'bg-sky-50 text-sky-700 border-sky-200',
  exceptie: 'bg-amber-50 text-amber-800 border-amber-200',
  trimis: 'bg-b4l-bg text-b4l-text-muted border-b4l-border',
  nou: 'bg-b4l-bg text-b4l-text-muted border-b4l-border',
}

export const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const zi = (s: string | null) => (s ? new Date(s).toLocaleDateString('ro-RO') : '—')

/** Numele curierului cum se scrie pe ecran: „Sameday", nu „SAMEDAY". */
export const numeCurier = (c: string) => (c === 'SAMEDAY' ? 'Sameday' : c)

/**
 * Mesajul serverului, oricum l-ar trimite: `{ error: { message } }` (erorile API) sau `{ message }`
 * (cateva rute vechi). Fara el, omul vedea „nu s-a putut" in loc de motivul exact.
 */
export function mesajServer(e: unknown, implicit: string): string {
  const d = (e as { response?: { data?: { message?: string; error?: { message?: string } } } })
    ?.response?.data
  return d?.error?.message ?? d?.message ?? implicit
}
